// Copyright (c) Docugami, Inc. All rights reserved.

import { IContextualMenuItem } from "@fluentui/react";
import { DisabledCss } from "components/common/DgCommonStyledComponents";
import { ExampleChunksModal } from "components/common/ExampleChunksModal";
import { RepeatCardHeader } from "components/reports/RepeatHeaders";
import { validateBinding } from "hooks/ChunkLinking";
import { useWaitCursor } from "hooks/WaitCursor";
import { useDocumentDgml } from "hooks/documentDgml";
import { useDropWithTrigger } from "hooks/dropWithTrigger";
import { useExampleChunksForLabel } from "hooks/exampleChunksForLabel";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useReportEntryActions } from "hooks/reportEntryActions";
import { useReportEntryItemSlots } from "hooks/reportEntrySlots";
import { useAddBinding } from "hooks/reports";
import { useCallback, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { createOperationId, setActiveAddEntryGroupRequest, useRemoveEntriesMutation } from "store/reports/reportsSlice";
import { selectIsReportPublished, selectOrderedReportEntryIds, selectReportDocumentId, selectReportEntryBindingsForCurrentDocument, selectSelectedLabel } from "store/reports/selectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { ActiveCardletDropTargetStyle, DisabledCardletStyle, DragWrapper, ItemCardletStyles } from "./CardStyles";
import { DndItem, DndReportItem, DndType, isDndChunk, isDndReportItem } from "./DndTypes";
import { ItemCardlet } from "./ItemCardlet";
import { ISlotItem, ReportChunkSlotItem, SlotItemType, isReportChunkSlot } from "./ItemSlot";
import { ItemSlots } from "./ItemSlots";
import { PlaceholderSlotVisual } from "./PlaceholderSlotVisual";
import { usePreviewChunksCommandProps } from "./PreviewChunksButton";
import { IReportEntryItem, IReportItem, ReportItemFooter } from "./ReportItemCardlet";
import { ReportEntryItemTitleInputField } from "./ReportItemTitleInputField";

const ContentRoot = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    ${props => !!props.disabled && DisabledCss};
`

interface ReportEntryItemCardletProps {
    item: IReportEntryItem;
    onClick?: (slotItem?: ISlotItem, childItem?: IReportItem) => void;
    dragDisabled?: boolean;
}

export const ReportEntryItemCardlet = (props: ReportEntryItemCardletProps): JSX.Element => {
    const [hovered, setHovered] = useState(false);
    const [exampleModalOpen, setExampleModalOpen] = useState(false);

    const isSelectable = !useAppSelector(selectSelectedLabel);

    const rootRef = useRef<HTMLDivElement | null>(null);
    const itemSlotRef = useRef<HTMLDivElement | null>(null);
    const dgml = useDocumentDgml();

    const { onClick } = props;
    const { entry, isDisabled } = props.item;
    const { trigger: triggerAddBinding } = useAddBinding(entry.reportId);
    const [triggerRemove, removeState] = useRemoveEntriesMutation();

    const { exampleChunks } = useExampleChunksForLabel(entry.title, false);

    const dispatch = useAppDispatch();

    useWaitCursor(removeState.isLoading);

    const onRemove = !!entry ? () => triggerRemove({ reportId: entry!.reportId, entryIds: [entry!.id] }) : undefined
    const { canRemoveEntry: canRemove } = useReportEntryActions();

    const folderId = useAppSelector(selectReportDocumentId)!;
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);
    const isPublished = useAppSelector(selectIsReportPublished);

    const header = <ReportEntryItemTitleInputField entry={entry} reportId={props.item.reportId} readonly={isPublished} />;
    const footer = <ReportItemFooter item={props.item} />;

    const { slots, isEmpty } = useReportEntryItemSlots({ item: props.item });

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndReportItem, any, { isDragging: boolean }>({
        type: DndType.ReportItem,
        item: {
            type: DndType.ReportItem,
            entryId: entry.id
        },
        canDrag: !props.dragDisabled,
        collect: m => ({
            isDragging: m.isDragging()
        })
    }, [entry]);

    const { triggerRef, dropTargetRef, isVisible, isOver } = useDropWithTrigger({
        dropTypes: DndType.Chunk,
        onDrop: async (item) => {
            if (isDndChunk(item)) {
                await triggerAddBinding({
                    folderId: folderId,
                    entryId: entry.id,
                    instanceId: item.instance.spanId
                });
            }
        },
        canDrop: item => !isEmpty && !props.item.isDisabled && isDndChunk(item) && dgml.isInitialized() && validateBinding(item.instance, documentBindings?.entities[entry.id]?.bindings)
    })

    const canDropForGroup = useCallback((dragItem: DndItem) =>
        !props.item.isDisabled && isDndReportItem(dragItem) && dragItem.entryId !== props.item.entry.id && !props.item.entry.groupId, [props.item])

    const [isTargetActive, targetRef] = useDrop<DndItem, any, boolean>({
        accept: DndType.ReportItem,
        canDrop: canDropForGroup,
        drop: async (item, m) => {
            if (isDndReportItem(item) && m.canDrop()) {
                // Give order precedence to the current, drop-target entry in the add group request
                const orderedEntries = !!entryIds ? [...entryIds] : undefined;
                if (!!orderedEntries) {
                    const draggedEntryIdx = orderedEntries.findIndex(x => x === item.entryId);
                    const draggedEntry = orderedEntries.splice(draggedEntryIdx, 1)[0];
                    const targetIdx = orderedEntries.findIndex(x => x === props.item.entry.id);
                    orderedEntries.splice(Math.min(targetIdx + 1, orderedEntries.length), 0, draggedEntry);
                }

                dispatch(setActiveAddEntryGroupRequest({
                    reportId: props.item.reportId,
                    label: "",
                    entries: [props.item.entry.id, item.entryId],
                    orderedEntries: orderedEntries,
                    operationId: createOperationId()
                }))
            }
        },
        collect: m => !!m.isOver() && !!m.getItem() && m.canDrop()
    });

    const onDrop = useCallback(async (item: ISlotItem, dndItem: DndItem) => {
        if (!isReportChunkSlot(item) || !isDndChunk(dndItem)) {
            return;
        }

        const instanceId = dndItem.instance.spanId!;
        await triggerAddBinding({
            entryId: props.item.entry.id,
            folderId: folderId,
            instanceId: instanceId,
            prevInstanceId: item.binding?.spanId
        })
    }, [triggerAddBinding, folderId, props.item.entry.id]);

    const bindings = documentBindings?.entities[entry.id]?.bindings;
    const canDrop = useCallback((item: ISlotItem, dropItem: DndItem): boolean => {
        return isReportChunkSlot(item) &&
            isDndChunk(dropItem) &&
            dgml.isInitialized() &&
            validateBinding(dropItem.instance, bindings);
    }, [dgml, bindings]);

    async function onClickPlaceholder() {
        props.onClick?.({
            id: '',
            type: SlotItemType.ReportChunk,
            entryId: entry.id
        } as ReportChunkSlotItem)
    }

    dragVisualRef(rootRef);
    triggerRef(rootRef);
    dropTargetRef(itemSlotRef);

    targetRef(rootRef);

    const cardletStyles: ItemCardletStyles | undefined = useMemo(() => {
        if (isDisabled) {
            return {
                root: [DisabledCardletStyle]
            }
        }
        if (!!isTargetActive) {
            return {
                root: [ActiveCardletDropTargetStyle]
            }
        }
    }, [isDisabled, isTargetActive])

    const isRepeatPlaceholderVisible = useMemo(() => {
        const addRepeatValid = !!bindings && bindings.filter(x => !!x.spanId).length > 0;
        return addRepeatValid && (isVisible || (props.item.isLinkable && hovered));
    }, [isVisible, props.item.isLinkable, hovered, bindings])

    const previewButtonProps = usePreviewChunksCommandProps({ entry: props.item.entry });
    const overflowCommands: IContextualMenuItem[] = useMemo(() => [
        {
            key: 'edit_label_button',
            name: 'Edit Label',
            disabled: !isSelectable,
            onClick: () => onClick?.()
        },
        previewButtonProps
    ], [previewButtonProps, isSelectable, onClick]);

    return (
        <DragWrapper
            isDragging={isDragging}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}>
            <ItemCardlet
                ref={rootRef}
                automationId={AutomationId.WorkPathDocumentItemCardlet}
                dragHandleRef={!props.dragDisabled ? dragRef : undefined}
                header={header}
                footer={footer}
                canRemove={canRemove}
                onRemove={onRemove}
                styles={cardletStyles}
                onExamplesClick={!!exampleChunks?.length ? () => setExampleModalOpen(true) : undefined}
                overflowCommands={overflowCommands}>
                <ContentRoot>
                    {slots.length > 1 && <RepeatCardHeader entryId={entry.id} folderId={folderId} count={isEmpty ? 0 : slots.length} />}
                    <ItemSlots slots={slots} onDrop={onDrop} canDrop={canDrop} onClick={props.onClick} scrollOnSelection={!!props.item.isSelected} />
                    <div ref={itemSlotRef}>
                        <PlaceholderSlotVisual
                            text="Drop a chunk here or click here to add a repeating instance of this item."
                            isVisible={isRepeatPlaceholderVisible}
                            isActive={isOver}
                            onClick={onClickPlaceholder} />
                    </div>
                </ContentRoot>
            </ItemCardlet>
            <ExampleChunksModal isOpen={exampleModalOpen} onDismiss={() => setExampleModalOpen(false)} label={entry.title} autoDismissWhenEmpty={true} />
        </DragWrapper>
    );
}
